import type { BonusVoicePack } from '../types/domain';

const EMPTY_VOICE_PACKS: BonusVoicePack[] = [];

export interface BonusVoicePackInputLike {
  voicePacks?: unknown;
  voicePackTitles?: unknown;
  voicePackContents?: unknown;
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function splitPipeSeparated(value: string): string[] {
  return value.split('|').map((part) => part.trim());
}

function normalizeVoicePack(input: unknown): BonusVoicePack {
  const pack = input && typeof input === 'object' ? (input as Partial<BonusVoicePack>) : {};
  return {
    title: readString(pack.title).trim(),
    contents: readString(pack.contents),
  };
}

export function hasExplicitBonusVoicePackInput(input: BonusVoicePackInputLike): boolean {
  return Array.isArray(input.voicePacks) || typeof input.voicePackTitles === 'string' || typeof input.voicePackContents === 'string';
}

export function bonusVoicePackTitlesValue(voicePacks: readonly BonusVoicePack[] | null | undefined): string {
  return Array.isArray(voicePacks) ? voicePacks.map((pack) => pack.title.trim()).join(' | ') : '';
}

export function bonusVoicePackContentsValue(voicePacks: readonly BonusVoicePack[] | null | undefined): string {
  return Array.isArray(voicePacks) ? voicePacks.map((pack) => pack.contents.trim()).join(' | ') : '';
}

export function validateBonusVoicePacks(voicePacks: readonly BonusVoicePack[] | null | undefined): string | null {
  if (!voicePacks || voicePacks.length === 0) {
    return 'Add at least one Title and one Content for this voice pack.';
  }

  for (const pack of voicePacks) {
    if (!pack.title.trim()) {
      return 'Remove any empty Title entries. Each "|" must have a title on both sides.';
    }
    if (!pack.contents.trim()) {
      return 'Remove any empty Content entries. Each "|" must have content on both sides.';
    }
  }

  return null;
}

export function parseBonusVoicePacks(
  voicePackTitles: string,
  voicePackContents: string
): { voicePacks: BonusVoicePack[]; error: string | null } {
  if (!voicePackTitles.trim() && !voicePackContents.trim()) {
    return {
      voicePacks: EMPTY_VOICE_PACKS,
      error: 'Add at least one Title and one Content for this voice pack.',
    };
  }

  const titles = splitPipeSeparated(voicePackTitles);
  const contents = splitPipeSeparated(voicePackContents);

  if (titles.length !== contents.length) {
    return {
      voicePacks: EMPTY_VOICE_PACKS,
      error: 'Add the same number of Titles and Contents so each voice prompt has one exact match.',
    };
  }

  const voicePacks = titles.map((title, index) => ({
    title,
    contents: contents[index] ?? '',
  }));
  const error = validateBonusVoicePacks(voicePacks);
  return { voicePacks: error ? EMPTY_VOICE_PACKS : voicePacks, error };
}

export function resolveBonusVoicePacks(
  input: BonusVoicePackInputLike,
  fallback?: readonly BonusVoicePack[] | null
): { voicePacks: BonusVoicePack[]; error: string | null } {
  if (typeof input.voicePackTitles === 'string' || typeof input.voicePackContents === 'string') {
    return parseBonusVoicePacks(readString(input.voicePackTitles), readString(input.voicePackContents));
  }

  const normalized = Array.isArray(input.voicePacks)
    ? input.voicePacks.map((pack) => normalizeVoicePack(pack))
    : Array.isArray(fallback)
      ? fallback.map((pack) => normalizeVoicePack(pack))
      : EMPTY_VOICE_PACKS;
  return {
    voicePacks: normalized,
    error: validateBonusVoicePacks(normalized),
  };
}
